import React, { useState } from 'react';
import { Drawer, Button, Menu, Badge, Tooltip } from 'antd';
import {
    MenuOutlined, ShoppingCartOutlined, UnorderedListOutlined,
    AppstoreOutlined, StarOutlined, HomeOutlined
} from '@ant-design/icons';
import { NavLink } from 'react-router-dom';

const MobileMenu = (props) => {
    const [visible, setVisible] = useState(false);

    const showDrawer = () => setVisible(true);
    const onClose = () => setVisible(false);

    const { userOrg, org, unreadOCHWithOrg, unreadOrderWithOrg, notShippedOrderWithOrg } = props;

    // ─── お助けカート menu ────────────────────────────────────────────────────
    // Exactly mirrors P4Header.js logic:
    //   DEFAULT             → full list (お助けカート一覧 + carts 1-5 + 送信履歴)
    //   userOrg.buyer       → override: history only + badge
    //   userOrg.reseller
    //     && org.maker      → override: history only (no badge)
    //   (all other reseller states keep default full list)

    // Default: full list — same as P4Header.js default before any if-blocks
    let menuOtasukeCart = (
        <Menu.SubMenu key="sub-otasuke" icon={<ShoppingCartOutlined />} title="お助けカート">
            <Menu.Item key="otasuke-carts">
                <NavLink to="/otasuke/carts">お助けカート一覧</NavLink>
            </Menu.Item>
            <Menu.Item key="otasuke-1">
                <NavLink to="/otasuke/cart/1">お助けカート1</NavLink>
            </Menu.Item>
            <Menu.Item key="otasuke-2">
                <NavLink to="/otasuke/cart/2">お助けカート2</NavLink>
            </Menu.Item>
            <Menu.Item key="otasuke-3">
                <NavLink to="/otasuke/cart/3">お助けカート3</NavLink>
            </Menu.Item>
            <Menu.Item key="otasuke-4">
                <NavLink to="/otasuke/cart/4">お助けカート4</NavLink>
            </Menu.Item>
            <Menu.Item key="otasuke-5">
                <NavLink to="/otasuke/cart/5">お助けカート5</NavLink>
            </Menu.Item>
            <Menu.Item key="otasuke-history">
                <NavLink to="/otasuke/history/carts">お助けカート送信履歴</NavLink>
            </Menu.Item>
        </Menu.SubMenu>
    );

    // Override 1: buyer → history only + unread badge (exactly P4Header.js lines 780-806)
    if (userOrg && userOrg.buyer) {
        menuOtasukeCart = (
            <Menu.SubMenu
                key="sub-otasuke"
                title={
                    <span>
                        <ShoppingCartOutlined />
                        <span style={{ marginLeft: 10 }}>お助けカート</span>
                        <Tooltip title={'未読'} color={'gray'}>
                            <Badge
                                style={{ marginLeft: 10 }}
                                color={'green'}
                                overflowCount={999}
                                count={(org && unreadOCHWithOrg && unreadOCHWithOrg[org.id]) || 0}
                                showZero={false}
                            />
                        </Tooltip>
                    </span>
                }
            >
                <Menu.Item key="otasuke-history">
                    <NavLink to="/otasuke/history/carts">
                        お助けカート一覧
                        <Tooltip title={'未読'} color={'gray'}>
                            <Badge
                                style={{ marginLeft: 10 }}
                                color={'green'}
                                overflowCount={999}
                                count={(org && unreadOCHWithOrg && unreadOCHWithOrg[org.id]) || 0}
                                showZero={false}
                            />
                        </Tooltip>
                    </NavLink>
                </Menu.Item>
            </Menu.SubMenu>
        );
    }

    // Override 2: reseller + org.maker → history only, no badge (exactly P4Header.js lines 808-834)
    if (userOrg && userOrg.reseller) {
        if (org && org.maker) {
            menuOtasukeCart = (
                <Menu.SubMenu
                    key="sub-otasuke"
                    title={
                        <span>
                            <ShoppingCartOutlined />
                            <span style={{ marginLeft: 10 }}>お助けカート</span>
                        </span>
                    }
                >
                    <Menu.Item key="otasuke-history">
                        <NavLink to="/otasuke/history/carts">
                            お助けカート一覧
                            <Tooltip title={'未読'} color={'gray'}>
                                <Badge
                                    style={{ marginLeft: 10 }}
                                    color={'green'}
                                    overflowCount={999}
                                    count={(unreadOCHWithOrg && unreadOCHWithOrg[org.id]) || 0}
                                    showZero={false}
                                />
                            </Tooltip>
                        </NavLink>
                    </Menu.Item>
                </Menu.SubMenu>
            );
        }
        // reseller + org.buyer or org=null → keeps default full list (same as P4Header.js)
    }



    // ─── カート (Cart 1-5) menu ───────────────────────────────────────────────
    // Matches P4Header.js logic:
    //   menuCart = <> </> when userOrg.maker
    //   menuCart = <> </> when userOrg.reseller && org is NOT maker (i.e. org.buyer sell mode)
    //   menuCart shows for: userOrg.buyer  OR  userOrg.reseller && org.maker
    let menuCart = null;

    const showCart = (userOrg && userOrg.buyer) ||
        (userOrg && userOrg.reseller && org && org.maker);

    if (showCart) {
        menuCart = (
            <Menu.SubMenu key="sub-cart" icon={<ShoppingCartOutlined />} title="カート">
                <Menu.Item key="cart-1"><NavLink to="/cart/1">カート1</NavLink></Menu.Item>
                <Menu.Item key="cart-2"><NavLink to="/cart/2">カート2</NavLink></Menu.Item>
                <Menu.Item key="cart-3"><NavLink to="/cart/3">カート3</NavLink></Menu.Item>
                <Menu.Item key="cart-4"><NavLink to="/cart/4">カート4</NavLink></Menu.Item>
                <Menu.Item key="cart-5"><NavLink to="/cart/5">カート5</NavLink></Menu.Item>
            </Menu.SubMenu>
        );
    }

    // ─── 発注書 / 注文書 menu ─────────────────────────────────────────────────
    // Matches P4Header.js: label is "注文書一覧" for maker OR reseller-in-sell-mode (org.buyer),
    // otherwise "発注書一覧"
    const isSellerMode =
        (userOrg && userOrg.maker) ||
        (userOrg && userOrg.reseller && org && org.buyer);

    const orderLabel = isSellerMode ? '注文書一覧' : '発注書一覧';

    const menuChumon = (
        <Menu.Item key="order" icon={<UnorderedListOutlined />}>
            <NavLink to="/order">
                {orderLabel}
                {org && (
                    <Tooltip title={'未読'} color={'gray'}>
                        <Badge
                            style={{ marginLeft: 8 }}
                            color={'green'}
                            overflowCount={999}
                            count={(unreadOrderWithOrg && unreadOrderWithOrg[org.id]) || 0}
                            showZero={false}
                        />
                    </Tooltip>
                )}
                {org && (
                    <Tooltip title={'未出荷'} color={'gray'}>
                        <Badge
                            style={{ marginLeft: 4 }}
                            color={'red'}
                            overflowCount={999}
                            count={(notShippedOrderWithOrg && notShippedOrderWithOrg[org.id]) || 0}
                            showZero={false}
                        />
                    </Tooltip>
                )}
            </NavLink>
        </Menu.Item>
    );

    return (
        <div className="mobile-menu-container">
            <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: '20px' }} />}
                onClick={showDrawer}
                style={{ height: '64px', width: '64px' }}
            />
            <Drawer
                title="メニュー"
                placement="left"
                onClose={onClose}
                open={visible}
                bodyStyle={{ padding: 0 }}
            >
                <Menu
                    mode="inline"
                    onClick={onClose}
                    style={{ borderRight: 0 }}
                >
                    <Menu.Item key="home" icon={<HomeOutlined />}>
                        <NavLink to="/">ホーム</NavLink>
                    </Menu.Item>
                    <Menu.Item key="items" icon={<AppstoreOutlined />}>
                        <NavLink to="/items">商品一覧</NavLink>
                    </Menu.Item>

                    <Menu.Item key="favorite" icon={<StarOutlined style={{ color: '#faad14' }} />}>
                        <NavLink to="/favorite/cart">お気に入り</NavLink>
                    </Menu.Item>

                    {menuOtasukeCart}

                    {menuChumon}

                    {menuCart}
                </Menu>
            </Drawer>
        </div>
    );
};

export default MobileMenu;
